import { clsx, type ClassValue } from "clsx";
import i18n from "i18next";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/uz-latn";
import "dayjs/locale/ru";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

/** dayjs tilini UI tiliga moslash (uz -> uz-latn) */
export function setDayjsLocale(lang: string) {
  dayjs.locale(lang === "uz" ? "uz-latn" : lang === "ru" ? "ru" : "en");
}

export { dayjs };

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Sana: Today 10:24 / Yesterday / Oct 4 */
export function formatWhen(iso: string | null | undefined, tz?: string): string {
  if (!iso) return "—";
  const d = tz ? dayjs(iso).tz(tz) : dayjs(iso);
  const now = tz ? dayjs().tz(tz) : dayjs();
  if (d.isSame(now, "day")) return `${i18n.t("time.today")} ${d.format("HH:mm")}`;
  if (d.isSame(now.subtract(1, "day"), "day")) return i18n.t("time.yesterday");
  if (d.isSame(now, "year")) return d.format("MMM D");
  return d.format("MMM D, YYYY");
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return dayjs(iso).format("MMM D, YYYY");
}

export function formatDateTime(iso: string | null | undefined, tz?: string): string {
  if (!iso) return "—";
  const d = tz ? dayjs(iso).tz(tz) : dayjs(iso);
  return d.format("MMM D, HH:mm");
}

export function formatMoney(value: number | string | null | undefined, currency = "USD"): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
}

/** Aniq summa: tiyin bo'lsa ko'rsatiladi (qoldiq $0.30 "$0" bo'lib qolmasin); KPI va yig'indilar `formatMoney` bilan yaxlit. */
export function formatMoneyExact(value: number | string | null | undefined, currency = "USD"): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  const digits = Number.isInteger(Math.round(n * 100) / 100) ? 0 : 2;
  return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
}

export function initialsOf(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

export function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
